import {
  useEffect,
  useMemo,
  useState,
} from 'react';

import type {
  Case,
} from '@/hooks/useCases';

import {
  type CaseParty,
  type ConflictResult,
  type LegalEvent,
  useLegalCore,
} from '@/hooks/useLegalCore';

type Props = {
  caseItem: Case;

  mode:
    | 'summary'
    | 'history';
};

const sideLabels:
  Record<string, string> = {
    client: 'Cliente',
    opposing: 'Parte contrária',
    third_party: 'Terceiro',
    other: 'Outro',
  };

const eventLabels:
  Record<string, string> = {
    case_created:
      'Caso criado',

    case_updated:
      'Caso atualizado',

    case_archived:
      'Caso arquivado',

    party_added:
      'Parte vinculada',

    party_removed:
      'Parte removida',

    case_party_added:
      'Parte vinculada',

    case_party_removed:
      'Parte removida',

    conflict_clear:
      'Conflito verificado',

    conflict_review_required:
      'Revisão de conflito necessária',
  };

function formatDateTime(
  value: string | null | undefined
) {
  if (!value) {
    return '—';
  }

  const parsed =
    Date.parse(value);

  if (!Number.isFinite(parsed)) {
    return value;
  }

  return new Date(
    parsed
  ).toLocaleString(
    'pt-BR'
  );
}

function getEventLabel(
  event: LegalEvent
) {
  return (
    event.title ||
    eventLabels[
      event.event_type
    ] ||
    event.event_type
  );
}

function ProcessIdentification({
  caseItem,
}: {
  caseItem: Case;
}) {
  const isCnj =
    caseItem.caseNumberKind ===
    'cnj';

  const validityLabel =
    !isCnj
      ? 'Identificador externo'
      : caseItem.caseNumberValid ===
          true
        ? 'CNJ válido'
        : caseItem.caseNumberValid ===
            false
          ? 'CNJ com dígito verificador inválido'
          : 'CNJ não validado';

  const validityClass =
    !isCnj
      ? 'bg-background-100 text-foreground-600'
      : caseItem.caseNumberValid ===
          true
        ? 'bg-emerald-50 text-emerald-700'
        : caseItem.caseNumberValid ===
            false
          ? 'bg-red-50 text-red-700'
          : 'bg-amber-50 text-amber-700';

  return (
    <div className="rounded-lg border border-background-200 p-3 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-foreground-400">
            Identificação processual
          </p>

          <p className="text-sm font-semibold text-foreground-800 mt-1">
            {caseItem.caseNumber ||
              'Sem número informado'}
          </p>
        </div>

        <span
          className={`px-2 py-1 rounded-md text-[10px] font-semibold whitespace-nowrap ${validityClass}`}
        >
          {validityLabel}
        </span>
      </div>

      {isCnj && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <div>
            <p className="text-[10px] text-foreground-400">
              Ano
            </p>

            <p className="text-xs font-medium text-foreground-700">
              {caseItem.filingYear ??
                '—'}
            </p>
          </div>

          <div>
            <p className="text-[10px] text-foreground-400">
              Segmento
            </p>

            <p className="text-xs font-medium text-foreground-700">
              {caseItem.judiciarySegment ??
                '—'}
            </p>
          </div>

          <div>
            <p className="text-[10px] text-foreground-400">
              Tribunal
            </p>

            <p className="text-xs font-medium text-foreground-700">
              {caseItem.tribunalCode ??
                '—'}
            </p>
          </div>

          <div>
            <p className="text-[10px] text-foreground-400">
              Origem
            </p>

            <p className="text-xs font-medium text-foreground-700">
              {caseItem.originUnitCode ??
                '—'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function PartiesSection({
  parties,
}: {
  parties: CaseParty[];
}) {
  return (
    <div className="space-y-2">
      <div>
        <p className="text-[10px] uppercase tracking-wider text-foreground-400">
          Partes e polos
        </p>

        <p className="text-xs text-foreground-500 mt-0.5">
          Relações estruturadas vinculadas ao caso.
        </p>
      </div>

      {parties.length === 0 ? (
        <div className="rounded-lg bg-background-100 p-3 text-xs text-foreground-500">
          Nenhuma parte estruturada encontrada.
        </div>
      ) : (
        parties.map(
          (party) => (
            <div
              key={party.id}
              className="rounded-lg border border-background-200 px-3 py-2.5 flex items-start justify-between gap-3"
            >
              <div>
                <p className="text-sm font-medium text-foreground-800">
                  {party.name}
                </p>

                <p className="text-[11px] text-foreground-500 mt-0.5">
                  {sideLabels[
                    party.side
                  ] ||
                    party.side}

                  {party.role
                    ? ` · ${party.role}`
                    : ''}
                </p>

                {party.document && (
                  <p className="text-[11px] text-foreground-400 mt-0.5">
                    {party.document}
                  </p>
                )}
              </div>

              {party.is_primary ===
                1 && (
                <span className="text-[10px] px-2 py-1 rounded bg-primary-50 text-primary-700 whitespace-nowrap">
                  Principal
                </span>
              )}
            </div>
          )
        )
      )}
    </div>
  );
}

function ConflictSection({
  caseItem,
  result,
  checking,
  onCheck,
}: {
  caseItem: Case;
  result: ConflictResult | null;
  checking: boolean;
  onCheck: () => void;
}) {
  if (!caseItem.opposingParty) {
    return null;
  }

  return (
    <div className="rounded-lg border border-background-200 p-3 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-foreground-400">
            Conflito de interesses
          </p>

          <p className="text-xs text-foreground-500 mt-1">
            Verifica vínculos anteriores da parte contrária em outros casos.
          </p>
        </div>

        <button
          type="button"
          disabled={checking}
          onClick={onCheck}
          className="px-2.5 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-700 hover:bg-background-100 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {checking
            ? 'Verificando...'
            : 'Verificar'}
        </button>
      </div>

      {result && (
        <div
          className={
            result.status ===
            'clear'
              ? 'rounded-lg bg-emerald-50 px-3 py-2.5'
              : 'rounded-lg bg-amber-50 px-3 py-2.5'
          }
        >
          <p
            className={
              result.status ===
              'clear'
                ? 'text-xs font-semibold text-emerald-800'
                : 'text-xs font-semibold text-amber-800'
            }
          >
            {result.status ===
            'clear'
              ? 'Nenhum vínculo anterior encontrado fora deste caso.'
              : `${result.matchCount} correspondência(s) encontrada(s). Revisão humana necessária.`}
          </p>

          {result.matches.length >
            0 && (
            <div className="mt-2 space-y-1.5">
              {result.matches
                .slice(0, 5)
                .map(
                  (match) => (
                    <div
                      key={`${match.entity_id}-${match.case_id ?? 'entity'}`}
                      className="text-[11px] text-amber-800"
                    >
                      <span className="font-medium">
                        {match.name}
                      </span>

                      {match.case_title
                        ? ` · ${match.case_title}`
                        : ''}

                      {match.case_number
                        ? ` · ${match.case_number}`
                        : ''}
                    </div>
                  )
                )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function CaseLegalCorePanel({
  caseItem,
  mode,
}: Props) {
  const {
    loadCaseParties,
    loadCaseEvents,
    checkConflict,
  } = useLegalCore();

  const [
    parties,
    setParties,
  ] =
    useState<CaseParty[]>(
      []
    );

  const [
    events,
    setEvents,
  ] =
    useState<LegalEvent[]>(
      []
    );

  const [
    loading,
    setLoading,
  ] =
    useState(true);

  const [
    error,
    setError,
  ] =
    useState<string | null>(
      null
    );

  const [
    checkingConflict,
    setCheckingConflict,
  ] =
    useState(false);

  const [
    conflictResult,
    setConflictResult,
  ] =
    useState<ConflictResult | null>(
      null
    );

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError(null);
    setConflictResult(null);

    void Promise.all([
      loadCaseParties(
        caseItem.id
      ),

      loadCaseEvents(
        caseItem.id
      ),
    ])
      .then(
        ([
          loadedParties,
          loadedEvents,
        ]) => {
          if (cancelled) {
            return;
          }

          setParties(
            loadedParties
          );

          setEvents(
            loadedEvents
          );
        }
      )
      .catch(
        (loadError: any) => {
          if (cancelled) {
            return;
          }

          setError(
            loadError?.message ||
              'Não foi possível carregar o núcleo jurídico do caso.'
          );
        }
      )
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    caseItem.id,
    loadCaseEvents,
    loadCaseParties,
  ]);

  const timelineEvents =
    useMemo(
      () =>
        events.filter(
          (event) =>
            event.event_kind ===
              'timeline' ||
            event.event_kind ===
              'conflict_check'
        ),
      [events]
    );

  const auditEvents =
    useMemo(
      () =>
        events.filter(
          (event) =>
            event.event_kind ===
            'audit'
        ),
      [events]
    );

  const handleConflictCheck =
    async () => {
      if (
        checkingConflict ||
        !caseItem.opposingParty
      ) {
        return;
      }

      setCheckingConflict(
        true
      );

      try {
        const result =
          await checkConflict(
            caseItem.opposingParty,
            null,
            caseItem.id
          );

        setConflictResult(
          result
        );

        const refreshed =
          await loadCaseEvents(
            caseItem.id
          );

        setEvents(
          refreshed
        );
      } catch (checkError: any) {
        setError(
          checkError?.message ||
            'Não foi possível verificar conflito.'
        );
      } finally {
        setCheckingConflict(
          false
        );
      }
    };

  if (loading) {
    return (
      <div className="rounded-lg border border-dashed border-background-200 p-3 text-xs text-foreground-500">
        Carregando dados jurídicos estruturados...
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
        {error}
      </div>
    );
  }

  if (mode === 'summary') {
    return (
      <div className="space-y-4">
        <ProcessIdentification
          caseItem={caseItem}
        />

        <PartiesSection
          parties={parties}
        />

        <ConflictSection
          caseItem={caseItem}
          result={conflictResult}
          checking={
            checkingConflict
          }
          onCheck={
            handleConflictCheck
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <p className="text-[10px] uppercase tracking-wider text-foreground-400 mb-2">
          Linha do tempo
        </p>

        {timelineEvents.length ===
        0 ? (
          <div className="rounded-lg bg-background-100 p-3 text-xs text-foreground-500">
            Nenhum evento operacional registrado.
          </div>
        ) : (
          <div className="space-y-2">
            {timelineEvents.map(
              (event) => (
                <div
                  key={event.id}
                  className="rounded-lg bg-background-100 p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-foreground-700">
                        {getEventLabel(
                          event
                        )}
                      </p>

                      {event.description && (
                        <p className="text-xs text-foreground-500 mt-1">
                          {event.description}
                        </p>
                      )}
                    </div>

                    <span className="text-[10px] text-foreground-400 whitespace-nowrap">
                      {formatDateTime(
                        event.occurred_at
                      )}
                    </span>
                  </div>

                  {(event.actor_name ||
                    event.actor_email) && (
                    <p className="text-[10px] text-foreground-400 mt-2">
                      {event.actor_name ||
                        event.actor_email}
                    </p>
                  )}
                </div>
              )
            )}
          </div>
        )}
      </div>

      <div>
        <p className="text-[10px] uppercase tracking-wider text-foreground-400 mb-2">
          Auditoria técnica
        </p>

        {auditEvents.length ===
        0 ? (
          <div className="rounded-lg bg-background-100 p-3 text-xs text-foreground-500">
            Nenhum evento de auditoria registrado.
          </div>
        ) : (
          <div className="space-y-2">
            {auditEvents.map(
              (event) => (
                <div
                  key={event.id}
                  className="rounded-lg border border-background-200 p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-medium text-foreground-700">
                        {getEventLabel(
                          event
                        )}
                      </p>

                      <p className="text-[10px] text-foreground-400 mt-1">
                        {event.event_type}
                      </p>
                    </div>

                    <span className="text-[10px] text-foreground-400 whitespace-nowrap">
                      {formatDateTime(
                        event.occurred_at
                      )}
                    </span>
                  </div>

                  {(event.actor_name ||
                    event.actor_email) && (
                    <p className="text-[10px] text-foreground-400 mt-2">
                      Responsável:{' '}
                      {event.actor_name ||
                        event.actor_email}
                    </p>
                  )}
                </div>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}
